import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import {
  deleteRequiredDoc,
  listRequiredDocs,
  updateRequiredDoc,
} from "@/features/required-docs/api";
import type { RequiredDoc, RequiredDocStatus } from "@/features/required-docs/types";
import { AddRequiredDocDialog } from "@/features/required-docs/components/add-required-doc-dialog";
import { ApiError } from "@/lib/api";
import { formatShortDate } from "@/lib/format";

const STATUS_META: Record<
  RequiredDocStatus,
  { label: string; variant: "secondary" | "default" | "outline" }
> = {
  PENDING: { label: "Pending", variant: "secondary" },
  RECEIVED: { label: "Received", variant: "default" },
  WAIVED: { label: "Waived", variant: "outline" },
};

const STATUS_OPTIONS: RequiredDocStatus[] = ["PENDING", "RECEIVED", "WAIVED"];

function errText(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

const COL_COUNT = 6;

interface RequiredDocsChecklistProps {
  clientId: string;
}

export function RequiredDocsChecklist({ clientId }: RequiredDocsChecklistProps) {
  const [docs, setDocs] = useState<RequiredDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RequiredDoc | null>(null);
  // The row whose status PATCH is in flight, and whether a delete is in flight —
  // used to disable the controls so a double-click can't fire a second request.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    listRequiredDocs(clientId)
      .then((rows) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setDocs(rows);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setDocs([]);
        setError(errText(err, "Failed to load required documents."));
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleStatusChange(doc: RequiredDoc, status: RequiredDocStatus) {
    if (status === doc.status || pendingId === doc.id) return;
    setPendingId(doc.id);
    try {
      const updated = await updateRequiredDoc(clientId, doc.id, { status });
      if (!mountedRef.current) return;
      setDocs((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      toast.success(`Marked ${STATUS_META[status].label}`);
    } catch (err) {
      if (!mountedRef.current) return;
      toast.error(errText(err, "Couldn't update status."));
    } finally {
      if (mountedRef.current) setPendingId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || deleting) return;
    const target = deleteTarget;
    setDeleting(true);
    try {
      await deleteRequiredDoc(clientId, target.id);
      if (!mountedRef.current) return;
      setDocs((prev) => prev.filter((d) => d.id !== target.id));
      toast.success("Required document removed");
    } catch (err) {
      if (!mountedRef.current) return;
      toast.error(errText(err, "Couldn't remove required document."));
    } finally {
      if (mountedRef.current) {
        setDeleting(false);
        setDeleteTarget(null);
      }
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-foreground mb-2 text-3xl font-bold">Required Docs</h1>
          <p className="text-muted-foreground max-w-2xl">
            Track the documents you&apos;ve requested from this client and their status.
          </p>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus /> Add required document
        </Button>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Document</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Due date</TableHead>
              <TableHead>Requested by</TableHead>
              <TableHead>Received at</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="py-10">
                  <Loader />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="text-destructive text-center">
                  {error}
                </TableCell>
              </TableRow>
            ) : docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="text-muted-foreground text-center">
                  No required documents yet. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              docs.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="text-foreground font-medium">
                    {doc.name}
                    {doc.notes && (
                      <span className="text-muted-foreground block text-xs font-normal">
                        {doc.notes}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_META[doc.status].variant}>
                      {STATUS_META[doc.status].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{formatShortDate(doc.dueDate)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {doc.requestedByName || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatShortDate(doc.receivedAt)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      <Select
                        value={doc.status}
                        disabled={pendingId === doc.id}
                        onValueChange={(v) => handleStatusChange(doc, v as RequiredDocStatus)}
                      >
                        <SelectTrigger
                          size="sm"
                          className="w-32"
                          aria-label={`Status for ${doc.name}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>
                              {STATUS_META[s].label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive"
                        aria-label={`Remove ${doc.name}`}
                        onClick={() => setDeleteTarget(doc)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <AddRequiredDocDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        clientId={clientId}
        onCreated={load}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove required document?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.name}” will be removed from this client&apos;s checklist. This can&apos;t
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting}>
              {deleting ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
